import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const BASE_URL = process.env.REACT_APP_API_URL;

const AcceptJoinRequest = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [message, setMessage] = useState('Processing your request...');
  const [loading, setLoading] = useState(true);
  const [requestData, setRequestData] = useState(null);
  const [errorDetails, setErrorDetails] = useState(null);
  
  // Add ref to prevent duplicate API calls
  const isProcessingRef = useRef(false);
  const hasProcessedRef = useRef(false);

  useEffect(() => {
    const token = searchParams.get('token');
    
    if (!token) {
      setStatus('error');
      setMessage('Invalid or missing token. Please check the link and try again.');
      setLoading(false);
      return;
    }

    // Prevent duplicate calls
    if (hasProcessedRef.current || isProcessingRef.current) {
      return;
    }

    // Call the backend API to accept the join request
    acceptJoinRequest(token);
  }, [searchParams]);

  const acceptJoinRequest = async (token) => {
    // Prevent multiple simultaneous calls
    if (isProcessingRef.current || hasProcessedRef.current) {
      return;
    }

    isProcessingRef.current = true;

    try {
      setLoading(true);
      setStatus('processing');
      setMessage('Processing your join request. This may take a few moments...');


      const response = await fetch(`${BASE_URL}/accept-factory-join-request?token=${token}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      // Always try to parse JSON response
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error('Failed to parse JSON response:', parseError);
        throw new Error('Invalid response format from server');
      }


      if (response.ok && data.success) {
        setStatus('success');
        setMessage(data.message || 'Join request accepted successfully!');
        setRequestData(data.data);
        setErrorDetails(null);
        hasProcessedRef.current = true; // Mark as successfully processed
      } else {
        // Handle different error status codes
        let errorMessage = data.message || 'Failed to process join request';
        
        // Special handling for already processed requests
        if (response.status === 409 && data.message && data.message.includes('already been')) {
          setStatus('success');
          setMessage('Join request has already been processed successfully!');
          setErrorDetails(null);
          hasProcessedRef.current = true;
          return;
        }
        
        switch (response.status) {
          case 400:
            errorMessage = data.message || 'Invalid request or missing token';
            break;
          case 404:
            errorMessage = data.message || 'Request not found or has already been processed';
            break;
          case 409:
            errorMessage = data.message || 'Individual company is already part of another factory';
            break;
          case 410:
            errorMessage = data.message || 'Request has expired (valid for 7 days)';
            break;
          case 500:
            errorMessage = 'Server error occurred. Please try again later or contact support.';
            break;
          default:
            errorMessage = data.message || 'An unexpected error occurred';
        }
        
        setStatus('error');
        setMessage(errorMessage);
        setErrorDetails(data);
      }
    } catch (error) {
      console.error('Error processing join request:', error);
      setStatus('error');
      
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        setMessage('Network error: Unable to connect to the server. Please check your internet connection and try again.');
      } else {
        setMessage('An unexpected error occurred while processing your request. Please try again later.');
      }
      
      setErrorDetails({ error: error.message });
    } finally {
      setLoading(false);
      isProcessingRef.current = false;
    }
  };

  const handleGoHome = () => {
    navigate('/');
  };

  const handleRetry = () => {
    const token = searchParams.get('token');
    if (token && !isProcessingRef.current) {
      // Reset processing flags for retry
      hasProcessedRef.current = false;
      isProcessingRef.current = false;
      acceptJoinRequest(token);
    }
  };

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-md-8 col-lg-6">
            <div className="card shadow-lg border-0">
              <div className="card-body p-5">
                <div className="text-center mb-4">
                  {loading && (
                    <div className="mb-3">
                      <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  )}
                  
                  {!loading && status === 'success' && (
                    <div className="mb-3">
                      <div className="text-success" style={{ fontSize: '4rem' }}>
                        ✅
                      </div>
                    </div>
                  )}
                  
                  {!loading && status === 'error' && (
                    <div className="mb-3">
                      <div className="text-danger" style={{ fontSize: '4rem' }}>
                        ❌
                      </div>
                    </div>
                  )}
                  
                  <h2 className="card-title mb-3">
                    {status === 'processing' && 'Processing Request'}
                    {status === 'success' && 'Request Accepted!'}
                    {status === 'error' && 'Request Failed'}
                  </h2>
                </div>

                <div className="text-center mb-4">
                  <p className="card-text text-muted fs-5">
                    {message}
                  </p>
                </div>

                {status === 'success' && requestData && (
                  <div className="alert alert-success" role="alert">
                    <h6 className="alert-heading">
                      <i className="bi bi-check-circle me-2"></i>
                      Successfully Processed!
                    </h6>
                    <p className="mb-2">The individual company has been successfully integrated into your factory network.</p>
                    
                    <div className="row mt-3">
                      <div className="col-md-6">
                        <strong>Company:</strong> {requestData.requesterName}<br/>
                        <strong>Branch Code:</strong> {requestData.requesterBranchCode}
                      </div>
                      <div className="col-md-6">
                        <strong>Factory Code:</strong> {requestData.factoryCode}<br/>
                        <strong>Integration Date:</strong> {new Date(requestData.integrationDate).toLocaleDateString()}
                      </div>
                    </div>
                    
                    <hr />
                    <p className="mb-0">
                      <strong>What happens next:</strong>
                    </p>
                    <ul className="mb-0 mt-2">
                      <li>All company data has been updated with your factory code</li>
                      <li>License expiry dates have been synchronized</li>
                      <li>Both parties have been notified via email</li>
                      <li>The company can now collaborate within your factory network</li>
                    </ul>
                  </div>
                )}

                {/* Show success message even without request data */}
                {status === 'success' && !requestData && (
                  <div className="alert alert-success" role="alert">
                    <h6 className="alert-heading">
                      <i className="bi bi-check-circle me-2"></i>
                      Successfully Processed!
                    </h6>
                    <p className="mb-0">The join request has been processed successfully. Both parties have been notified via email.</p>
                  </div>
                )}

                {status === 'error' && (
                  <div className="alert alert-danger" role="alert">
                    <h6 className="alert-heading">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      Request Could Not Be Processed
                    </h6>
                    <p className="mb-2">The join request could not be processed at this time.</p>
                    <hr />
                    <p className="mb-0">
                      <strong>Possible reasons:</strong>
                    </p>
                    <ul className="mb-0 mt-2">
                      <li>The request token has expired (valid for 7 days)</li>
                      <li>The request has already been processed</li>
                      <li>The individual company is already part of another factory</li>
                      <li>Network connectivity issues</li>
                      <li>Technical error occurred during processing</li>
                    </ul>
                    
                    {errorDetails && process.env.NODE_ENV === 'development' && (
                      <div className="mt-3">
                        <small className="text-muted">
                          <strong>Debug Info:</strong> {JSON.stringify(errorDetails, null, 2)}
                        </small>
                      </div>
                    )}
                  </div>
                )}

                <div className="text-center mt-4">
                  <div className="d-flex gap-2 justify-content-center flex-wrap">
                    <button 
                      className="btn btn-primary btn-lg px-4"
                      onClick={handleGoHome}
                      disabled={loading}
                    >
                      <i className="bi bi-house me-2"></i>
                      Go to Home
                    </button>
                    
                    {status === 'error' && !loading && !hasProcessedRef.current && (
                      <button 
                        className="btn btn-outline-secondary btn-lg px-4"
                        onClick={handleRetry}
                        disabled={isProcessingRef.current}
                      >
                        <i className="bi bi-arrow-clockwise me-2"></i>
                        Try Again
                      </button>
                    )}
                  </div>
                </div>

                {!loading && (
                  <div className="text-center mt-4">
                    <small className="text-muted">
                      If you continue to experience issues, please contact support or the requesting company directly.
                    </small>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AcceptJoinRequest;